/* Reads text from a photo of a visiting card (in the browser, nothing is uploaded).

   Cards come in every design: logos next to text, light text on dark / coloured bands, text in boxes,
   shadows in the photo. So the card is read in a few passes and the lines are merged:
     1. clean-up (flatten light, crop to the card) + "sparse text" OCR - every text line on its own
     2. if something is still missing: the same picture flipped (light text on dark)
     3. if something is still missing: normal page-layout OCR - finds text inside boxes / frames
     4. if almost nothing was found: the photo may be sideways / upside down - try rotated
   Most cards are finished after pass 1. */

import { analyzePixels, renderGray, planCrop } from './cardImage';
import { parseCard, hasBusinessWord } from './cardParser';

async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) { /* fall through to <img> */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not open the photo')); };
    img.src = url;
  });
}

/** Photo -> { normal(), inverted() } canvases ready for OCR. */
export async function prepareCard(file) {
  const bmp = await loadBitmap(file);
  let w = bmp.width || bmp.naturalWidth;
  let h = bmp.height || bmp.naturalHeight;
  const k = Math.max(w, h) > 2400 ? 2400 / Math.max(w, h) : 1;
  w = Math.round(w * k);
  h = Math.round(h * k);
  const work = document.createElement('canvas');
  work.width = w;
  work.height = h;
  const ctx = work.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bmp, 0, 0, w, h);
  const info = analyzePixels(ctx.getImageData(0, 0, w, h).data, w, h);
  const plan = planCrop(info, w, h);
  const make = (flip) => {
    const full = document.createElement('canvas');
    full.width = w;
    full.height = h;
    full.getContext('2d').putImageData(new ImageData(renderGray(info.d, info.s, flip, w, h), w, h), 0, 0);
    const out = document.createElement('canvas');
    out.width = plan.outW;
    out.height = plan.outH;
    const octx = out.getContext('2d');
    octx.imageSmoothingEnabled = true;
    octx.imageSmoothingQuality = 'high';
    const b = plan.box;
    octx.drawImage(full, b.x0, b.y0, b.x1 - b.x0 + 1, b.y1 - b.y0 + 1, 0, 0, plan.outW, plan.outH);
    return out;
  };
  return { normal: () => make(false), inverted: () => make(true), dark: info.dark };
}

function rotateCanvas(src, deg) {
  const turn = deg === 90 || deg === 270;
  const out = document.createElement('canvas');
  out.width = turn ? src.height : src.width;
  out.height = turn ? src.width : src.height;
  const ctx = out.getContext('2d');
  ctx.translate(out.width / 2, out.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return out;
}

const digitCount = (s) => s.replace(/\D/g, '').length;
const alnumCount = (s) => s.replace(/[^A-Za-z0-9]/g, '').length;

function linesFrom(data) {
  const out = [];
  ((data && data.blocks) || []).forEach((b) => (b.paragraphs || []).forEach((p) => (p.lines || []).forEach((l) => {
    const t = String(l.text || '').replace(/\s+/g, ' ').trim();
    const bb = l.bbox || {};
    if (alnumCount(t) >= 3 && (Number(l.confidence) >= 45 || digitCount(t) >= 8)) {
      out.push({ t, c: Number(l.confidence) || 0, x: bb.x0 || 0, y: bb.y0 || 0, h: (bb.y1 || 0) - (bb.y0 || 0) });
    }
  })));
  return out;
}

const sameText = (a, b) => {
  const x = a.toLowerCase().replace(/[^a-z0-9]/g, '');
  const y = b.toLowerCase().replace(/[^a-z0-9]/g, '');
  return x === y || (x.length > 4 && y.length > 4 && (x.includes(y) || y.includes(x)));
};

/** Adds the lines of `extra` that `base` does not already have. rowMode: skip any line that shares a text row. */
function mergeLines(base, extra, rowMode) {
  const out = base.slice();
  extra.forEach((l) => {
    const i = out.findIndex((o) => {
      const dy = Math.abs(o.y - l.y);
      const rowHit = dy < 0.6 * Math.max(o.h, l.h, 1);
      if (rowMode) return rowHit;
      return dy < 0.7 * Math.max(o.h, l.h, 1) && (sameText(o.t, l.t) || Math.abs(o.x - l.x) < 80);
    });
    if (i < 0) out.push(l);
    else if (digitCount(l.t) > digitCount(out[i].t) + 2) out[i] = l; /* a fuller phone number beats a clipped one */
    else if (!rowMode && l.c > out[i].c) out[i] = l;
  });
  return out;
}

const sortLines = (lines) => lines.slice().sort((a, b) => (Math.abs(a.y - b.y) < 12 ? a.x - b.x : a.y - b.y));
const asInput = (lines) => sortLines(lines).map((l) => ({ t: l.t, h: l.h }));

/** What is still missing after the passes so far? */
function gaps(lines) {
  const r = parseCard(asInput(lines));
  return {
    weak: !r.customer_mobile || !r.company || lines.length < 4 || !(r.customer_city || r.customer_state),
    firmWeak: !hasBusinessWord(r.company),
  };
}

/**
 * @param {File|Blob} file card photo
 * @param {(percent:number)=>void} [onProgress]
 * @param {{workerOptions?: object}} [opts] workerOptions: extra createWorker options (tests / self-hosted language data)
 * @returns {Promise<{text: string, lines: {t: string, h: number}[]}>}
 */
export async function readCardText(file, onProgress, opts = {}) {
  const prep = await prepareCard(file);
  const mod = await import('tesseract.js');
  const createWorker = mod.createWorker || (mod.default && mod.default.createWorker);
  let span = [0, 70];
  const worker = await createWorker('eng', 1, {
    logger: (m) => {
      if (onProgress && m && m.status === 'recognizing text') onProgress(Math.round(span[0] + (span[1] - span[0]) * (m.progress || 0)));
    },
    ...(opts.workerOptions || {}),
  });
  const run = async (canvas, psm, from, to) => {
    span = [from, to];
    await worker.setParameters({ tessedit_pageseg_mode: psm });
    const res = await worker.recognize(canvas, {}, { text: true, blocks: true });
    return linesFrom(res && res.data);
  };
  try {
    const normal = prep.normal();
    let lines = await run(normal, '11', 0, 70);
    let g = gaps(lines);
    if (g.weak || prep.dark > 0.12) {
      /* only rows the first pass found nothing on (white text on a dark band); never overwrite a good line */
      const flipped = (await run(prep.inverted(), '11', 70, 85)).filter((l) => l.c >= 60);
      lines = mergeLines(lines, flipped, true);
      g = gaps(lines);
    }
    if (g.weak || g.firmWeak) {
      lines = mergeLines(lines, await run(normal, '3', 85, 97), true);
      g = gaps(lines);
    }
    if (!parseCard(asInput(lines)).customer_mobile && lines.length >= 3) {
      /* phone number still missing: read the picture again, digits only */
      await worker.setParameters({ tessedit_char_whitelist: '0123456789+()- ' });
      const digitsOnly = await run(normal, '11', 97, 99);
      await worker.setParameters({ tessedit_char_whitelist: '' });
      lines = mergeLines(lines, digitsOnly.filter((l) => digitCount(l.t) >= 8), false);
    }
    if (lines.length < 3 || !parseCard(asInput(lines)).customer_mobile) {
      let best = lines;
      const tries = [90, 270, 180];
      for (let t = 0; t < tries.length; t += 1) {
        const got = await run(rotateCanvas(normal, tries[t]), '11', 97, 99);
        if (got.length > best.length) best = got;
        if (best.length >= 6) break;
      }
      lines = best;
    }
    const sorted = sortLines(lines);
    return { text: sorted.map((l) => l.t).join('\n'), lines: sorted.map((l) => ({ t: l.t, h: l.h })) };
  } finally {
    await worker.terminate();
  }
}
